/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { AppBar, createStyles, IconButton, ListItem, ListItemIcon, ListItemText, makeStyles, Theme, Toolbar, Typography } from "@material-ui/core";
import { Add } from '@material-ui/icons';
import CloseIcon from "@material-ui/icons/Close";
import React, { useMemo, useRef, useState, useLayoutEffect, CSSProperties, useEffect } from "react";
import { useSelector } from "react-redux";
import { FixedSizeList as List } from "react-window";
import { AppState } from "../../redux";
import { Manager } from '../../redux/users';
import { useDataValue } from './form';
import Search from "./Search";

/**
 * Label for list item that allows evaluator to add a new facility manager to the system
 */
export const NEW_OWNER_NAME = 'Add New Owner';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
      // height: "calc(100% + 96px)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    toolbarButton: {
      marginRight: theme.spacing(2),
    },
    toolbarTitle: {
      marginRight: theme.spacing(4),
      flexShrink: 0,
    },
    search: {
      maxWidth: theme.breakpoints.width("sm"),
      width: "100%",
    },
    list: {
      height: "calc(100% + 96px)",
      WebkitOverflowScrolling: "touch",
    },
  })
);

interface OwnerSelectProps {
  onSelect: (facility: Manager | null) => void;
}

function managerSearchFilter(text: string) {
  const exp = new RegExp(text, "i");
  return function (manager: Manager) {
    return exp.test(manager.name as string);
  };
}

const ITEM_SIZE = 52;

export default (props: OwnerSelectProps) => {
  const classes = useStyles(props);
  const facilityId = useDataValue<string>('{/facility/id}');
  const facilities = useSelector((state: AppState) => state.facilities.items);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [filter, setFilter] = useState<string>("");

  const filteredManagers = useMemo(() => {
    if (!managers) {
      return [];
    }
    return managers
      .filter(managerSearchFilter(filter))
      .concat([{
        name: NEW_OWNER_NAME,
        id: null,
        email: null,
        phone: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        company: null,
        subscribed: true,
        new: true
      }]);

  }, [managers, filter]);

  const rootRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (facilities && facilityId && facilities[facilityId]) {
      setManagers(facilities[facilityId].farm.managers as any[]);
    }
  }, [facilities, facilityId])

  useLayoutEffect(() => {
    if (rootRef.current) {
      setHeight((rootRef.current as any).clientHeight);
    }
  }, [height])

  return (
    <div className={classes.root}>
      <AppBar position="sticky">
        <Toolbar color="primary" variant="dense">
          <IconButton
            className={classes.toolbarButton}
            edge="start"
            color="inherit"
            onClick={() => props.onSelect(null)}
          >
            <CloseIcon />
          </IconButton>
          <Typography className={classes.toolbarTitle} variant="h6">
            {/* use prop here as well */}
            Select Owner
          </Typography>
          <Search className={classes.search} onSearchUpdated={setFilter} showClear />
        </Toolbar>
      </AppBar>

      <div className={classes.list} ref={rootRef}>
        <List
          itemSize={ITEM_SIZE}
          itemCount={filteredManagers.length}
          height={height}
          width="100%"
        >
          {({ index, style }) => {
            const f = JSON.parse(JSON.stringify(filteredManagers[index]));  // copy since we are going to mutate

            return (
              <ListItem style={style as CSSProperties} key={f.name} button onClick={() => props.onSelect(f)}>
                <ListItemText primary={f.name} />
                {/* todo just use a prop here for this constant, then can use same component for manager and owner select elements */}
                {f.name === NEW_OWNER_NAME ? (
                  <ListItemIcon>
                    <Add />
                  </ListItemIcon>
                ) : null}
              </ListItem>
            );
          }}
        </List>
      </div>
    </div>
  );
};
